'use client';

import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';
import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { imageSource } from '@eliadmin/core/src/utils/image-alt-utils';
import { resolveFerviDetailImageAlt } from '@eliadmin/core/src/utils/fervi-product-image-alt';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { lazy, Suspense, useMemo, useRef, useState } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const Lightbox = lazy(() =>
  import('@eliadmin/core/src/components/lightbox/lightbox-component').then(module => ({
    default: module.LightboxComponent,
  })),
);

export function ProductImages() {
  const onlyVariantImages = useGlobalStore(
    s => s.templateSettings.product_detail?.gallery_with_image_variants?.enabled ?? false,
  );
  const product = useVariantsStore(s => s.product);
  const selectedVariant = useVariantsStore(s => s.selectedVariant);
  const images = useMemo(
    () => collectGalleryImages(onlyVariantImages, product, selectedVariant),
    [onlyVariantImages, product, selectedVariant],
  );
  // Reset selection and native scroll together when product/variant images change.
  return (
    <Gallery
      key={JSON.stringify(images.map(image => imageSource(image)))}
      images={images}
      name={product.name}
    />
  );
}

type GalleryImage = ReturnType<typeof collectGalleryImages>[number];

function Gallery({ images, name }: { images: GalleryImage[]; name: string }) {
  const { t } = useTranslations();
  const railRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const visibleImages = images.slice(0, 4);
  const goTo = (index: number) => {
    const rail = railRef.current;
    if (!rail || !visibleImages.length) return;
    const next = (index + visibleImages.length) % visibleImages.length;
    rail.scrollTo({
      left: next * rail.clientWidth,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  if (!images.length)
    return (
      <Image
        src="/images/placeholder.png"
        alt={name}
        width={600}
        height={600}
        className="aspect-square max-h-[500px] w-full object-contain"
      />
    );

  return (
    <div className="ea-product-detail-images flex w-full min-w-0 items-start gap-2">
      <div className="hidden max-h-[500px] w-20 shrink-0 flex-col gap-2 overflow-y-auto md:flex">
        {visibleImages.map((image, index) => (
          <button
            key={imageSource(image)}
            type="button"
            onClick={() => goTo(index)}
            aria-label={t('text3532') + ' ' + (index + 1)}
            aria-current={index === active}
            className={cn(
              'h-20 w-20 shrink-0 rounded-[6px] border bg-white p-1',
              index === active && 'border-foreground',
            )}
          >
            <Image
              src={imageSource(image)!}
              alt=""
              width={80}
              height={80}
              sizes="80px"
              loading="lazy"
              className="h-full w-full object-contain"
            />
          </button>
        ))}
        {images.length > 4 && (
          <button
            type="button"
            onClick={() => setLightboxIndex(4)}
            aria-label={t('text3534')}
            className="h-20 w-20 shrink-0 rounded-[6px] bg-primary text-background"
          >
            +{images.length - 4}
          </button>
        )}
      </div>
      <div className="relative aspect-square max-h-[500px] min-w-0 flex-1 overflow-hidden rounded-[6px] border bg-white">
        <div
          ref={railRef}
          className="absolute inset-0 flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          onScroll={event => {
            const rail = event.currentTarget;
            if (rail.clientWidth) setActive(Math.round(rail.scrollLeft / rail.clientWidth));
          }}
          onKeyDown={event => {
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              event.preventDefault();
              goTo(active + (event.key === 'ArrowLeft' ? -1 : 1));
            }
          }}
        >
          {visibleImages.map((image, index) => (
            <button
              key={imageSource(image)}
              type="button"
              onClick={() => setLightboxIndex(index)}
              aria-label={t('text3532') + ' ' + (index + 1) + ': ' + name}
              className="relative h-full w-full shrink-0 snap-start bg-white"
            >
              <Image
                src={imageSource(image)!}
                alt={resolveFerviDetailImageAlt(image, name)}
                fill
                sizes="(min-width: 1280px) 610px, (min-width: 768px) 48vw, calc(100vw - 32px)"
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'auto'}
                className="object-contain"
              />
            </button>
          ))}
        </div>
        {visibleImages.length > 1 && (
          <div className="pointer-events-none absolute inset-x-2 bottom-2 flex items-center justify-between">
            <button
              type="button"
              aria-label={t('text3525')}
              onClick={() => goTo(active - 1)}
              className="pointer-events-auto h-11 w-11 rounded-full border bg-white/90"
            >
              ←
            </button>
            <span className="rounded-full bg-white/90 px-3 py-1 text-sm" aria-live="polite">
              {active + 1} / {images.length}
            </span>
            <button
              type="button"
              aria-label={t('text3526')}
              onClick={() => goTo(active + 1)}
              className="pointer-events-auto h-11 w-11 rounded-full border bg-white/90"
            >
              →
            </button>
          </div>
        )}
      </div>
      {lightboxIndex !== null && (
        <Suspense fallback={null}>
          <Lightbox
            isOpen
            setIsOpen={open => {
              if (!open) setLightboxIndex(null);
            }}
            images={[...images.slice(lightboxIndex), ...images.slice(0, lightboxIndex)]}
            zoom={{ maxZoomPixelRatio: 8 }}
          />
        </Suspense>
      )}
    </div>
  );
}

export function collectGalleryImages(
  onlyVariantImages: boolean,
  product: ProductDetailsWithSlug,
  selectedVariant?: VariantsEntity,
) {
  const variantImages = Array.isArray(selectedVariant?.images) ? selectedVariant.images : [];
  const productImages = Array.isArray(product?.images) ? product.images : [];
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const source =
    onlyVariantImages && variantImages.some(image => imageSource(image))
      ? variantImages
      : [
          product?.image,
          ...productImages,
          ...variants.flatMap(variant => (Array.isArray(variant?.images) ? variant.images : [])),
        ];
  const seen = new Set<string>();
  return source.filter((image): image is NonNullable<typeof image> => {
    const url = imageSource(image);
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}
